import { useCallback, useState } from 'react';

interface AsyncState<TData> {
  data: TData | null;
  error: Error | null;
  loading: boolean;
}

export const useAsync = <TData, TArgs extends unknown[]>(
  asyncFn: (...args: TArgs) => Promise<TData>,
) => {
  const [state, setState] = useState<AsyncState<TData>>({
    data: null,
    error: null,
    loading: false,
  });

  const execute = useCallback(
    async (...args: TArgs): Promise<TData> => {
      setState((prev) => ({ ...prev, loading: true, error: null }));
      try {
        const data = await asyncFn(...args);
        setState({ data, error: null, loading: false });
        return data;
      } catch (error) {
        const normalizedError = error instanceof Error ? error : new Error('Unknown async error');
        setState((prev) => ({ ...prev, error: normalizedError, loading: false }));
        throw normalizedError;
      }
    },
    [asyncFn],
  );

  const reset = useCallback(() => {
    setState({ data: null, error: null, loading: false });
  }, []);

  return {
    data: state.data,
    error: state.error,
    loading: state.loading,
    execute,
    reset,
  };
};
